import { TemplatesCustom } from '@/types/generated-types/graphql';
import { getShopDefaultLang, UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import type { IntegrationOwnedItem } from '@/types/integrations';

/**
 * Extends `IntegrationOwnedItem` so the picker can pass instances straight
 * to `useIntegrationGating`. `author` is narrowed to the same union used
 * by all gallery items — the backend `applyIntegrationEnrichment` always
 * stamps one of the two values, never an arbitrary string.
 */
export interface GalleryTemplateItem extends IntegrationOwnedItem {
  path: string;
  title: Record<string, string>;
  description?: Record<string, string>;
  previews: string[];
  htmlPreviewCount?: number;
  isGalleryItem: true;
}

export async function getTemplatesBaseGalleryList(
  templateType: string,
  idShop?: number,
): Promise<GalleryTemplateItem[]> {
  // idShop lets the backend resolve `shop_integration_status` for items
  // attributed to integrations. Legacy callers without it still receive
  // valid items, just with shop_integration_status: null.
  const response = await nestCall(
    'get',
    NestApiType.DEFAULT,
    `/workflow/base-config/${templateType}_base_list`,
    {},
    '',
    undefined,
    true,
    idShop ? { idShop } : {},
  );

  const items = response?.value || [];

  return items.map((item: any) => ({
    ...item,
    isGalleryItem: true as const,
  }));
}

export interface FetchUserTemplatesResult {
  data: any[];
  total: number;
}

export async function fetchUserTemplates(
  labelSearch: string,
  category: string,
  type: string,
  campaignType: string | null,
  limit: number,
  offset = 0,
  campaignId?: number,
): Promise<FetchUserTemplatesResult> {
  if (category === 'gallery') {
    // Forward idShop to unlock per-shop `shop_integration_status` resolution
    // for items published by integrations.
    const idShop = UserState.activeShop?.id;
    const items = await getTemplatesBaseGalleryList(
      type,
      typeof idShop === 'number' ? idShop : undefined,
    );
    return { data: items, total: items.length };
  }

  const queryParams = new URLSearchParams({
    type,
    category,
    limit: String(limit),
    offset: String(offset),
  });
  if (labelSearch) {
    queryParams.set('label', labelSearch);
  }
  if (campaignId) {
    queryParams.set('campaignId', String(campaignId));
  }

  const response = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    '/templates/selector',
    {},
    `?${queryParams.toString()}`,
  );

  if (response?.success && Array.isArray(response.data)) {
    return { data: response.data, total: response.total ?? response.data.length };
  }

  return { data: [], total: 0 };
}

export async function fetchRecursiveTemplate(idShop: number, idTemplate: number) {
  const response = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/templates/${idTemplate}/chain`,
    {},
    '',
    undefined,
    true,
  );

  if (!response?.success || !Array.isArray(response.data) || response.data.length === 0) {
    return [];
  }

  return response.data as TemplatesCustom[];
}
